import { Paper, Typography } from "@mui/material";
import BrandConfigurationReportHead from "./BrandConfigurationReportHead";
import BrandConfigurationReportTable from "./BrandConfigurationReportTable/BrandConfigurationReportTable";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  setBrandConfigReportLoader,
  setIsFetchedBrandConfigReport,
  setPage,
  setSelectedAffiliatedSystem,
  setSelectedBrandName,
  setSelectedIntegrationStatus,
  setSelectedReason,
  setSelectedStuckAt,
} from "../../../../features/brandConfigurationReportSlice";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  selectUserWebsitePermissions,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";
import { BrandConfigReportEndPointLogic } from "../../../../logic/Partners/DataIntegrations/BrandConfigReportEndPointLogic";
import BrandConfigReportSkeleton from "../BrandSkeletons/BrandConfigReportSkeleton";
import { useSearchParams } from "react-router-dom";
import { setSelectedWebsite } from "../../../../features/websiteActionSlice";
import { Box, Grid } from "@mui/system";

const BrandConfigurationReport = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const websitePermissions = useSelector(selectUserWebsitePermissions) as any;

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isFetchedBrandConfigReport } = useSelector(
    (state: any) => state.brandConfigurationReport,
  );
  // Get 1st website_shared_uid according to curernt requirements
  const website_shared_uid = websitePermissions?.[0]
    ? (Object.keys(websitePermissions[0])?.[0] ?? "")
    : "";
  const getBrandConfigurationReportList = async () => {
    if (!website_shared_uid || website_shared_uid === "") return;
    dispatch(setBrandConfigReportLoader(true));
    dispatch(setIsFetchedBrandConfigReport(false));
    try {
      await Promise.all([
        BrandConfigReportEndPointLogic.getBrandConfigPIECharts(
          oauthTokenHeader,
          website_shared_uid,
        ),
        BrandConfigReportEndPointLogic.getBrandConfigurationReport(
          oauthTokenHeader,
        ),
      ]);
    } catch (err) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetch brand configuration report. Please try again.",
          type: "error",
        }),
      );
    } finally {
      dispatch(setBrandConfigReportLoader(false));
      dispatch(setIsFetchedBrandConfigReport(true));
    }
  };

  // Helper function to parse filters from URL
  const parseFiltersFromURL = (filtersParam: string) => {
    const filters: Record<string, string[]> = {};
    const filterPairs = filtersParam.split("&");

    filterPairs.forEach((pair) => {
      const [key, value] = pair.split("=");
      if (key && value !== undefined) {
        filters[key] = decodeURIComponent(value).split(",");
      }
    });

    return filters;
  };

  // Fetch the brand configuration report list on component mount
  useEffect(() => {
    if (!website_shared_uid || website_shared_uid === "") return;
    getBrandConfigurationReportList();
    // eslint-disable-next-line
  }, [website_shared_uid]);

  //default filter set as Not Ready if no filters in URL and handle URL params
  useEffect(() => {
    const filtersParam = searchParams.get("filters");
    const searchParam = searchParams.get("search");
    const pageParam = searchParams.get("page");

    // Handle page parameter - convert to number
    if (pageParam) {
      const parsed = parseInt(pageParam, 10);
      if (!isNaN(parsed)) dispatch(setPage(parsed));
    } else {
      // Reset to page 0 if no page param exists
      dispatch(setPage(0));
    }

    if (filtersParam || searchParam) {
      // Handle search parameter
      if (searchParam) {
        dispatch(setSelectedBrandName(searchParam));
      }

      // Handle filters parameter
      if (filtersParam) {
        const filters = parseFiltersFromURL(filtersParam);

        if (filters.IntegrationStatus) {
          dispatch(setSelectedIntegrationStatus(filters.IntegrationStatus));
        }
        if (filters.StuckAt) {
          dispatch(setSelectedStuckAt(filters.StuckAt));
        }
        if (filters.Reason) {
          dispatch(setSelectedReason(filters.Reason));
        }
        if (filters.AffiliatedSystem) {
          dispatch(setSelectedAffiliatedSystem(filters.AffiliatedSystem));
        }
        if (filters.Websites) {
          dispatch(setSelectedWebsite(filters.Websites));
        }
      }

      // Clean up unwanted params but keep existing filters
      const updatedParams = new URLSearchParams(searchParams);
      ["brandSharedUid", "bkInterfaceSharedUid", "brandId", "step"].forEach(
        (key) => {
          if (updatedParams.has(key)) updatedParams.delete(key);
        },
      );

      // Only update if params were actually removed
      if (
        searchParams.has("brandSharedUid") ||
        searchParams.has("bkInterfaceSharedUid") ||
        searchParams.has("brandId") ||
        searchParams.has("step")
      ) {
        setSearchParams(updatedParams, { replace: true });
      }
    } else {
      // Set default filter
      dispatch(setSelectedIntegrationStatus(["Not Ready"]));
      const updatedParams = new URLSearchParams(searchParams);
      ["brandSharedUid", "bkInterfaceSharedUid", "brandId", "step"].forEach(
        (key) => {
          if (updatedParams.has(key)) updatedParams.delete(key);
        },
      );
      updatedParams.set("filters", "IntegrationStatus=Not Ready");
      updatedParams.set("page", "0");
      setSearchParams(updatedParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {!website_shared_uid ? (
        <Box sx={{ padding: " 16px 16px 20px  16px" }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid size={7}>
              <Typography variant="h3" sx={{ fontSize: "24px", mb: "5px" }}>
                Brand Configuration Report
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "14px" }}>
                Track Each Brand’s Integration Progress and Resolve
                Configuration Bottlenecks to Ensure Smooth Data Flow.
              </Typography>
            </Grid>
          </Grid>
          <Paper
            sx={{
              width: "100%",
              height: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#ffffff",
              boxShadow: "none",
              border: "1px solid rgba(224, 224, 224, 1)",
            }}
          >
            <Typography variant="body1" color="textSecondary">
              No data available to display.
            </Typography>
          </Paper>
        </Box>
      ) : isFetchedBrandConfigReport ? (
        <Paper>
          <BrandConfigurationReportHead />
          <BrandConfigurationReportTable />
        </Paper>
      ) : (
        <BrandConfigReportSkeleton />
      )}
    </>
  );
};

export default BrandConfigurationReport;
